/** Товар 1С в окне «Добавить цену»: карточка только для чтения (что за товар — по-человечески, ссылки
 *  1С свёрнуты) и поиск по проданным с начала себестоимости (`GET /products?q=`). */
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useId, useState } from "react";

import { searchProducts, type MissingOffer, type ProductItem } from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { characteristic1c, dateRu } from "./format";
import { KEY } from "./queries";
import { Badge, ErrorBox } from "./ui";

/** Товар 1С (номенклатура × характеристика) — из «Без цены» или из поиска. */
export type Product1c = Omit<ProductItem, "has_price" | "price_valid_from">;

export const PRODUCT_MIN_Q = 2;

function OfferLine({ o }: { o: MissingOffer }): React.ReactElement {
  return (
    <div>
      <span className="uppercase">{o.mp}</span> {o.mp_sku}
      {o.vendor_code && ` · ${o.vendor_code}`}
      {o.name && <span className="text-muted-foreground"> · {o.name}</span>}
    </div>
  );
}

/** Карточка товара: артикул и название 1С, код, характеристика словами, офферы МП; GUID — под
 *  свёрнутым «ссылки 1С». Ничего не редактируется: это данные 1С. */
export function ProductCard({
  product,
  action,
}: {
  product: Product1c;
  /** кнопка справа («Выбрать другой») */
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5 rounded-md border bg-muted/30 px-3 py-2 text-sm" data-testid="product-card">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div>
            <b>{product.article_1c ?? "артикул в 1С не указан"}</b>
            {product.name_1c && <span> {product.name_1c}</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            {product.code_1c ? `код ${product.code_1c}` : "код 1С не указан"}
            {" · "}Характеристика: {characteristic1c(product.characteristic_ref, product.characteristic_name)}
          </div>
        </div>
        {action}
      </div>
      {product.offers.length > 0 && (
        <div className="text-xs">
          <div className="text-muted-foreground">Продаётся на МП:</div>
          {product.offers.map((o, i) => (
            <OfferLine key={`${o.mp}:${o.mp_sku}:${o.vendor_code ?? ""}:${o.name ?? ""}:${i}`} o={o} />
          ))}
        </div>
      )}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer select-none">ссылки 1С</summary>
        <div className="mt-1 space-y-0.5 break-all">
          <div>
            номенклатура: <code>{product.nomenclature_ref}</code>
          </div>
          <div>
            характеристика: <code>{product.characteristic_ref}</code>
          </div>
        </div>
      </details>
    </div>
  );
}

/** Поиск товара по артикулу / названию / коду 1С (от 2 символов, с задержкой ввода). Выбор —
 *  `onPick(товар, cogs_from)`. */
export function ProductSearch({
  onPick,
  error,
}: {
  onPick: (p: Product1c, cogsFrom: string) => void;
  /** «выберите товар» после попытки отправить */
  error: string | null;
}): React.ReactElement {
  const inputId = useId();
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(draft.trim()), 300);
    return () => clearTimeout(t);
  }, [draft]);
  const enabled = q.length >= PRODUCT_MIN_Q;
  const res = useQuery({
    queryKey: KEY.products(q),
    queryFn: () => searchProducts(q),
    enabled,
    retry: false,
  });
  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="text-sm font-medium">
        Товар
      </label>
      <Input
        id={inputId}
        placeholder="артикул, название или код 1С"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {!enabled && (
        <p className="text-xs text-muted-foreground">
          Ищем среди товаров, проданных с начала себестоимости. Введите от {PRODUCT_MIN_Q} символов.
        </p>
      )}
      {enabled && res.isLoading && <p className="text-xs text-muted-foreground">ищу…</p>}
      {enabled && res.isError && <ErrorBox error={res.error} what="Поиск товара не удался" />}
      {enabled && res.data && res.data.items.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="product-none">
          Ничего не найдено среди проданных с начала себестоимости. Товар ещё не продавался — введите ссылки
          1С вручную (ниже).
        </p>
      )}
      {enabled && res.data && res.data.items.length > 0 && (
        <ul className="max-h-64 divide-y overflow-auto rounded-md border" data-testid="product-results">
          {res.data.items.map((p) => (
            <li key={`${p.nomenclature_ref}:${p.characteristic_ref}`}>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => onPick(p, res.data.cogs_from)}
                data-testid="product-option"
              >
                <span className="flex items-baseline gap-2">
                  <b>{p.article_1c ?? "без артикула"}</b>
                  <span className="min-w-0 flex-1 truncate">{p.name_1c ?? ""}</span>
                  {p.has_price && (
                    <Badge
                      cls="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                      title="у товара уже есть цена, действующая с начала себестоимости"
                    >
                      есть цена{p.price_valid_from ? ` с ${dateRu(p.price_valid_from)}` : ""}
                    </Badge>
                  )}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {p.code_1c ? `код ${p.code_1c} · ` : ""}Характеристика:{" "}
                  {characteristic1c(p.characteristic_ref, p.characteristic_name)}
                </span>
              </button>
            </li>
          ))}
          {res.data.truncated && (
            <li className="px-3 py-1.5 text-xs text-muted-foreground">
              Показаны первые {res.data.items.length} (по выручке) — уточните запрос.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Кнопка «Выбрать другой» для карточки выбранного товара. */
export function ChangeProduct({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <Button type="button" size="sm" variant="outline" className="h-7 shrink-0" onClick={onClick}>
      Выбрать другой
    </Button>
  );
}
